import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { useWorkspaces } from "../workspace/useWorkspaces";

export interface EntitlementRow {
  plan_key: string;
  plan_name: string;
  subscription_status: string;
  feature_key: string;
  enabled: boolean;
  limit_value: number | null;
}

/**
 * Plan entitlements for the active workspace, resolved server-side by
 * public.get_workspace_entitlements (membership-checked, security definer).
 *
 * UI gating only, server-side enforcement lives in RLS/functions via
 * private.workspace_has_feature as modules require it. Never trust the
 * client to enforce a plan.
 */
export const useEntitlements = () => {
  const { activeWorkspace } = useWorkspaces();
  const workspaceId = activeWorkspace?.workspaceId ?? null;

  const { data, isPending } = useQuery({
    queryKey: ["gravun", "entitlements", workspaceId],
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
    queryFn: async (): Promise<EntitlementRow[]> => {
      const { data, error } = await getSupabaseClient().rpc(
        "get_workspace_entitlements",
        { p_workspace_id: workspaceId },
      );
      if (error) throw error;
      return (data ?? []) as EntitlementRow[];
    },
  });

  const rows = data ?? [];
  const planKey = rows[0]?.plan_key ?? null;
  const planName = rows[0]?.plan_name ?? null;
  const subscriptionStatus = rows[0]?.subscription_status ?? null;

  const hasFeature = useCallback(
    (featureKey: string) =>
      rows.some((r) => r.feature_key === featureKey && r.enabled),
    [rows],
  );

  /** null = unlimited (or feature not present, check hasFeature first). */
  const featureLimit = useCallback(
    (featureKey: string) =>
      rows.find((r) => r.feature_key === featureKey)?.limit_value ?? null,
    [rows],
  );

  return {
    isPending,
    planKey,
    planName,
    subscriptionStatus,
    entitlements: rows,
    hasFeature,
    featureLimit,
  };
};
